import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts';
import { EChartsOption } from 'echarts';
import { DatasetDistribution } from '../../../catalogue/reference-datasets/hackathon-datasets.data';

@Component({
  selector: 'app-reference-dataset-distribution-chart',
  standalone: true,
  imports: [CommonModule, NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './reference-dataset-distribution-chart.component.html',
  styleUrl: './reference-dataset-distribution-chart.component.scss',
})
export class ReferenceDatasetDistributionChartComponent implements OnChanges {
  @Input({ required: true }) distribution!: DatasetDistribution;

  chartOptions: EChartsOption = {};
  chartHeight = 220;

  ngOnChanges(): void {
    const categoryCount = this.distribution?.categories.length ?? 0;
    this.chartHeight = categoryCount > 8 ? 320 : categoryCount > 4 ? 260 : 220;
    this.chartOptions = this.buildChartOptions();
  }

  private buildChartOptions(): EChartsOption {
    const categories = this.distribution.categories.map((item) => item.name);
    const values = this.distribution.categories.map((item) => item.count);
    const descriptions = new Map(
      this.distribution.categories
        .filter((item) => item.description)
        .map((item) => [item.name, item.description as string]),
    );
    const useShortLabels = categories.every((name) => name.length <= 2);
    const needsRotation = !useShortLabels && categories.some((name) => name.length > 12);

    return {
      animation: false,
      grid: {
        left: useShortLabels ? 16 : 8,
        right: 16,
        top: 24,
        bottom: needsRotation ? 48 : useShortLabels ? 16 : 8,
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const point = Array.isArray(params) ? params[0] : params;
          const data = point as { name?: string; value?: number };
          const label = data.name ?? '';
          const description = descriptions.get(label);
          const title = description ? `${label} — ${description}` : label;
          return `${title}: ${data.value ?? 0}`;
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          interval: 0,
          rotate: needsRotation ? 24 : 0,
          align: useShortLabels ? 'center' : undefined,
          fontSize: useShortLabels ? 12 : 11,
          fontWeight: useShortLabels ? 600 : 400,
          color: '#64748b',
        },
        axisLine: { lineStyle: { color: '#e2e8f0' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLabel: { color: '#64748b', fontSize: 11 },
        splitLine: { lineStyle: { color: '#f1f5f9' } },
      },
      series: [
        {
          type: 'bar',
          data: values,
          barMaxWidth: 48,
          itemStyle: {
            color: '#14868d',
            borderRadius: [4, 4, 0, 0],
          },
        },
      ],
    };
  }
}
